import type { DocumentKind } from './kinds.ts';
import type { TemplateSection } from './sections.ts';
import { PRIVACY_POLICY, SUBSCRIPTION_OFFER } from './starter-site.ts';

/**
 * Стартовые тексты документов (раздел 17): пишем мы, админ проверяет с юристом и публикует. Пока вид документа
 * не опубликован, редактор открывается с этим текстом, а документы этого вида не собираются. В тексте — только
 * подстановки своего вида (`variablesOf`).
 */

type Paragraph = string | readonly string[];

const paragraph = (text: string) => ({ type: 'paragraph', content: [{ type: 'text', text }] });

/** Строка — абзац, массив строк — маркированный список. */
const node = (item: Paragraph) =>
  typeof item === 'string'
    ? paragraph(item)
    : {
        type: 'bulletList',
        content: item.map((text) => ({ type: 'listItem', content: [paragraph(text)] })),
      };

const section = (title: string, ...items: Paragraph[]): TemplateSection => ({
  title,
  body: { type: 'doc', content: items.map(node) },
});

/** Условие о ПЭП (раздел 18): `owner` — чья платформа отправляет коды, в родительном падеже. */
const pepClause = (owner: string): string[] => [
  'Стороны договорились подписывать документы простой электронной подписью (ПЭП) в смысле Федерального закона от 06.04.2011 № 63-ФЗ «Об электронной подписи».',
  `Ключ ПЭП — одноразовый код из шести цифр, который платформа ${owner} отправляет на адрес электронной почты подписанта. Ввод кода на странице подписания означает подписание всех документов пакета, показанных перед вводом кода.`,
  'Документ, подписанный ПЭП, равнозначен документу на бумаге, подписанному собственноручно. Подписанный документ не изменяется: платформа хранит его снимок, хэш SHA-256 и лист подписания — кто, когда, с какого адреса почты и IP-адреса подписал.',
  'Каждая сторона хранит доступ к своей почте в тайне и сразу сообщает другой стороне, если доступ утрачен. Действия, совершённые с кодом из письма, считаются совершёнными владельцем адреса почты.',
];

const OFFER: TemplateSection[] = [
  section(
    'Общие положения',
    'Настоящий документ — публичная оферта {studio.party} (далее — Исполнитель) о заключении договора на оказание услуг по разработке, дизайну и сопровождению цифровых продуктов на условиях ниже.',
    'Оферта действует в версии {offer.version} от {offer.date}. Каждая опубликованная версия постоянно доступна по адресу {offer.url}.',
    'Акцептом оферты является подписание Заказчиком Заявки. С момента подписания Заявки договор считается заключённым на условиях оферты в версии, указанной в Заявке; последующие версии оферты на заключённый договор не влияют.',
  ),
  section('Термины', [
    'Заказчик — лицо, подписавшее Заявку.',
    'Заявка — документ, в котором Стороны согласуют проект, состав услуг, сроки и стоимость; подписывается по оферте и является её акцептом.',
    'Спецификация — приложение к Заявке: этапы, результаты, сроки, стоимость и график оплат.',
    'Результат — дизайн-макеты, программный код, тексты и иные материалы, созданные Исполнителем по Заявке.',
    'Акт — документ о приёмке услуг этапа или доли графика оплат.',
  ]),
  section(
    'Предмет договора',
    'Исполнитель оказывает услуги по Заявке, а Заказчик принимает и оплачивает их. Состав услуг, результаты, сроки и стоимость определяются Заявкой и Спецификацией.',
    'Изменение состава, сроков или стоимости оформляется дополнительным соглашением к Заявке, которое Стороны подписывают так же, как Заявку.',
  ),
  section(
    'Порядок оказания услуг и приёмки',
    'Услуги оказываются по этапам Спецификации. По окончании этапа Исполнитель направляет Заказчику Акт.',
    'Заказчик в течение 5 (пяти) рабочих дней подписывает Акт или направляет мотивированные замечания. Если в этот срок Заказчик не подписал Акт и не направил замечания, услуги этапа считаются принятыми.',
    'Замечания, не противоречащие Заявке и Спецификации, Исполнитель устраняет в разумный срок без дополнительной оплаты.',
  ),
  section(
    'Стоимость и порядок оплаты',
    'Стоимость услуг указана в Заявке. Оплата производится по графику Спецификации на основании счетов Исполнителя в течение 5 (пяти) рабочих дней со дня выставления счёта.',
    'Налог на добавленную стоимость начисляется в соответствии с налоговым режимом Исполнителя и указывается в Заявке и счетах.',
    'При задержке оплаты Исполнитель вправе приостановить оказание услуг; сроки этапов сдвигаются на время задержки.',
  ),
  section('Обязанности сторон', [
    'Исполнитель оказывает услуги качественно и в сроки Спецификации, сообщает Заказчику о ходе работ и о риске срыва сроков.',
    'Заказчик своевременно предоставляет материалы, доступы и ответы на вопросы Исполнителя, принимает и оплачивает услуги.',
    'Сроки этапов сдвигаются на время, в течение которого Заказчик не предоставлял необходимые материалы или ответы.',
  ]),
  section(
    'Права на результат',
    'Исключительные права на Результат переходят к Заказчику в полном объёме с момента полной оплаты услуг соответствующего этапа. До оплаты Заказчик вправе использовать Результат только для ознакомления.',
    'Исполнитель вправе упоминать проект и показывать Результат в своём портфолио, если Заявкой не установлено иное.',
  ),
  section(
    'Конфиденциальность',
    'Стороны не раскрывают третьим лицам сведения, полученные друг от друга в связи с договором и не являющиеся общедоступными, в течение срока договора и 3 (трёх) лет после его окончания.',
  ),
  section(
    'Ответственность',
    'Стороны отвечают за неисполнение обязательств в соответствии с законодательством Российской Федерации. Ответственность Исполнителя ограничена стоимостью услуг этапа, по которому возникло нарушение.',
    'Стороны освобождаются от ответственности при обстоятельствах непреодолимой силы, если сообщили о них другой стороне в течение 5 (пяти) рабочих дней.',
  ),
  section('Электронная подпись и обмен документами', ...pepClause('Исполнителя')),
  section(
    'Срок действия, изменение оферты и разрешение споров',
    'Оферта действует до её отзыва Исполнителем. Новая версия оферты публикуется по тому же адресу и применяется к Заявкам, подписанным после её публикации.',
    'Договор действует до полного исполнения обязательств по Заявке. Споры решаются переговорами, а при недостижении согласия — в суде по месту нахождения Исполнителя; досудебный порядок обязателен, срок ответа на претензию — 10 (десять) рабочих дней.',
  ),
];

const ORDER: TemplateSection[] = [
  section(
    '',
    '{client.party} (далее — Заказчик) подписанием настоящей Заявки принимает публичную оферту {studio.party} (далее — Исполнитель) версии {offer.version} от {offer.date}, размещённую по адресу {offer.url}, и поручает Исполнителю оказать услуги на условиях оферты и настоящей Заявки.',
  ),
  section(
    'Проект и услуги',
    'Проект: {project.title}. Состав услуг, результаты и сроки этапов приведены в Спецификации (Приложение № 1 к Заявке), которая является её неотъемлемой частью.',
  ),
  section(
    'Стоимость и сроки',
    'Стоимость услуг — {amount} ({amount_words}), {vat_note}.',
    'Срок оказания услуг — {term}. Оплата — по графику Спецификации.',
  ),
  section(
    'Подписание',
    'Заявка подписывается простой электронной подписью в порядке, установленном офертой: сначала Исполнитель, затем Заказчик. Заявка вступает в силу с момента её подписания Заказчиком.',
    'Контакт Заказчика для документов и кодов подписи — {signer.email}.',
  ),
];

const SPECIFICATION: TemplateSection[] = [
  section(
    '',
    'Спецификация является Приложением № 1 к Заявке № {order.number} от {order.date} и её неотъемлемой частью. Проект: {project.title}.',
  ),
  section(
    'Итог',
    'Общая стоимость услуг — {amount} ({amount_words}), {vat_note}. Срок оказания услуг — {term}.',
    'Этапы, результаты, сроки и график оплат приведены в таблицах ниже.',
  ),
];

const INVOICE: TemplateSection[] = [
  section(
    'Условия оплаты',
    'Оплата по Заявке № {order.number} от {order.date}: {stage}.',
    'Счёт действителен 5 (пять) банковских дней. В назначении платежа укажите номер счёта.',
  ),
];

const ACT: TemplateSection[] = [
  section(
    '',
    '{studio.party} (Исполнитель) и {client.party} (Заказчик) составили настоящий Акт о том, что Исполнитель оказал, а Заказчик принял услуги по Заявке № {order.number} от {order.date} к оферте версии {offer.version}: {stage}.',
  ),
  section(
    'Итог',
    'Стоимость оказанных услуг — {amount} ({amount_words}), {vat_note}.',
    'Услуги оказаны в полном объёме и в срок. Заказчик претензий по объёму, качеству и срокам оказания услуг не имеет.',
  ),
];

const AMENDMENT: TemplateSection[] = [
  section(
    '',
    '{studio.party} (Исполнитель) и {client.party} (Заказчик) договорились внести в Заявку № {order.number} от {order.date} к оферте версии {offer.version} следующие изменения.',
  ),
  section(
    'Изменения',
    'Опишите, что меняется: сроки, состав услуг или стоимость. Если меняется состав или цена, к соглашению прикладывается новая Спецификация.',
    'Стоимость услуг с учётом изменений — {amount} ({amount_words}), {vat_note}. Срок оказания услуг — {term}.',
  ),
  section(
    'Остальные условия',
    'Остальные условия Заявки и оферты остаются без изменений. Соглашение подписывается простой электронной подписью и вступает в силу с момента его подписания Заказчиком.',
  ),
];

/** Общие разделы договоров с исполнителями (самозанятый, ИП, ГПХ). */
const contractorTerms = (paymentNote: string): TemplateSection[] => [
  section(
    'Предмет договора',
    'Исполнитель оказывает Заказчику услуги по заданиям Заказчика (дизайн, разработка, тестирование, управление проектами и иные услуги по профилю Исполнителя), а Заказчик принимает и оплачивает их.',
    'Задания, объём и сроки согласуются в платформе Заказчика. Задание, принятое Исполнителем в работу, обязательно для обеих сторон.',
  ),
  section(
    'Приёмка и оплата',
    'Оказанные услуги за период фиксируются в Акте о выплате: задачи, часы и сумма. Акт подписывают обе стороны простой электронной подписью.',
    paymentNote,
  ),
  section(
    'Права на результаты',
    'Исключительные права на результаты услуг переходят к Заказчику в полном объёме с момента подписания Акта о выплате. Вознаграждение за отчуждение прав включено в стоимость услуг.',
  ),
  section(
    'Конфиденциальность',
    'Исполнитель не раскрывает сведения о Заказчике, его клиентах и проектах, ставшие ему известными в связи с договором, в течение срока договора и 3 (трёх) лет после его окончания.',
  ),
  section('Электронная подпись', ...pepClause('Заказчика')),
  section(
    'Срок действия',
    'Договор вступает в силу с момента подписания обеими сторонами и действует бессрочно. Каждая сторона вправе расторгнуть договор, уведомив другую сторону за 14 (четырнадцать) дней; оказанные до расторжения услуги оплачиваются.',
  ),
];

const EMPLOYEE_NPD: TemplateSection[] = [
  section(
    '',
    '{studio.party} (далее — Заказчик) и {employee.name}, ИНН {employee.inn}, применяющий специальный налоговый режим «Налог на профессиональный доход» с {employee.npd_date} (далее — Исполнитель), заключили настоящий договор оказания услуг.',
  ),
  ...contractorTerms(
    'Заказчик оплачивает услуги по Акту в течение 5 (пяти) рабочих дней на счёт Исполнителя. Исполнитель формирует чек в приложении «Мой налог» и передаёт его Заказчику в течение 3 (трёх) дней после оплаты; при утрате статуса плательщика налога на профессиональный доход Исполнитель сообщает об этом Заказчику в тот же день.',
  ),
];

const EMPLOYEE_IP: TemplateSection[] = [
  section(
    '',
    '{studio.party} (далее — Заказчик) и индивидуальный предприниматель {employee.name}, ИНН {employee.inn}, ОГРНИП {employee.ogrnip} (далее — Исполнитель), заключили настоящий договор оказания услуг.',
  ),
  ...contractorTerms(
    'Заказчик оплачивает услуги по Акту в течение 5 (пяти) рабочих дней на расчётный счёт Исполнителя. Налоги с вознаграждения Исполнитель уплачивает самостоятельно.',
  ),
];

const EMPLOYEE_GPH: TemplateSection[] = [
  section(
    '',
    '{studio.party} (далее — Заказчик) и {employee.name}, паспорт {employee.passport}, адрес регистрации: {employee.address} (далее — Исполнитель), заключили настоящий договор возмездного оказания услуг.',
  ),
  ...contractorTerms(
    'Заказчик оплачивает услуги по Акту в течение 5 (пяти) рабочих дней на банковский счёт Исполнителя. Заказчик как налоговый агент удерживает и перечисляет налог на доходы физических лиц и уплачивает страховые взносы в порядке, установленном законом.',
  ),
];

const EMPLOYEE_STAFF: TemplateSection[] = [
  section(
    '',
    '{studio.party} (далее — Работодатель) и {employee.name}, паспорт {employee.passport}, адрес регистрации: {employee.address} (далее — Работник), заключили настоящий трудовой договор.',
  ),
  section(
    'Предмет договора',
    'Работник принимается на работу на должность «{employee.position}» и выполняет обязанности по этой должности. Договор заключён на неопределённый срок. Дата начала работы — дата подписания договора, если стороны не договорились об иной дате.',
  ),
  section(
    'Режим работы и оплата труда',
    'Работнику устанавливается пятидневная рабочая неделя продолжительностью 40 часов; работа может выполняться дистанционно. Должностной оклад, порядок и сроки выплаты заработной платы — в соответствии с положением об оплате труда Работодателя; заработная плата выплачивается не реже чем каждые полмесяца.',
    'Работнику предоставляется ежегодный оплачиваемый отпуск продолжительностью 28 календарных дней.',
  ),
  section(
    'Права и обязанности',
    'Права и обязанности сторон определяются Трудовым кодексом Российской Федерации, локальными актами Работодателя и настоящим договором. Работник соблюдает конфиденциальность сведений о Работодателе, его клиентах и проектах.',
  ),
  section(
    'Экземпляры договора',
    'Договор составлен на бумаге в двух экземплярах, по одному для каждой стороны; юридическую силу имеют бумажные экземпляры, подписанные собственноручно. Электронная копия подписывается простой электронной подписью для учёта в платформе Работодателя, скан бумажного экземпляра хранится вместе с ней.',
  ),
];

const PAYOUT_ACT: TemplateSection[] = [
  section(
    '',
    '{studio.party} (Заказчик) и {employee.name} (Исполнитель) составили настоящий Акт к договору № {contract.number} от {contract.date} о том, что за период {period} Исполнитель оказал услуги, перечисленные в таблице, а Заказчик принял их.',
  ),
  section(
    'Итог',
    'Сумма вознаграждения — {amount} ({amount_words}). Услуги оказаны в полном объёме, стороны претензий друг к другу не имеют.',
    'Подтверждение оплаты (чек или платёжное поручение) прикладывается к Акту.',
  ),
];

/** Согласие на обработку персональных данных (152-ФЗ): на сайте его не было — пишем коротко, админ проверяет с юристом. */
const PD_CONSENT: TemplateSection[] = [
  section(
    '',
    'Отправляя заявку, отклик или иную форму на сайте {studio.site}, входя в личный кабинет или иным образом передавая свои данные, я свободно, своей волей и в своём интересе даю {studio.party} (далее — Оператор) согласие на обработку моих персональных данных на условиях ниже.',
  ),
  section('Какие данные', [
    'фамилия, имя, отчество;',
    'номер телефона, адрес электронной почты, ссылки на профили и портфолио;',
    'сведения, которые я сам сообщаю в сообщениях и прикладываемых файлах;',
    'технические данные: IP-адрес, сведения о браузере и устройстве, cookie.',
  ]),
  section('Цели обработки', [
    'ответ на заявку, подготовка коммерческого предложения, заключение и исполнение договора;',
    'рассмотрение отклика на вакансию и связь по нему;',
    'работа личного кабинета, уведомления о ходе работ, счетах и документах;',
    'улучшение работы сайта и сервиса.',
  ]),
  section(
    'Действия с данными',
    'Сбор, запись, систематизация, накопление, хранение, уточнение, извлечение, использование, передача (предоставление, доступ) лицам, привлечённым Оператором для исполнения указанных целей, обезличивание, блокирование, удаление и уничтожение — с использованием средств автоматизации и без них.',
  ),
  section(
    'Срок и отзыв согласия',
    'Согласие действует до достижения целей обработки или до его отзыва. Отозвать согласие можно письмом на адрес {studio.email}; после отзыва Оператор прекращает обработку и уничтожает данные в срок, установленный законом, кроме случаев, когда обработка допускается без согласия.',
    'Порядок обработки и защиты данных описан в Политике в отношении обработки персональных данных Оператора.',
  ),
];

const STARTERS: Readonly<Record<DocumentKind, readonly TemplateSection[]>> = {
  offer: OFFER,
  order: ORDER,
  specification: SPECIFICATION,
  invoice: INVOICE,
  act: ACT,
  amendment: AMENDMENT,
  employee_npd: EMPLOYEE_NPD,
  employee_ip: EMPLOYEE_IP,
  employee_gph: EMPLOYEE_GPH,
  employee_staff: EMPLOYEE_STAFF,
  payout_act: PAYOUT_ACT,
  subscription_offer: SUBSCRIPTION_OFFER,
  privacy_policy: PRIVACY_POLICY,
  pd_consent: PD_CONSENT,
};

/** Стартовый текст вида документа. */
export const starterSections = (kind: DocumentKind): readonly TemplateSection[] => STARTERS[kind];
